import {
    APP_INITIALIZER,
    EnvironmentProviders,
    ErrorHandler,
    inject,
    makeEnvironmentProviders
} from '@angular/core';
import { Router } from '@angular/router';
import defaultsDeep from '@df/util/fn/defaults-deep';
import { DeepPartial } from '@df/util/type/deep-partial';
import * as Sentry from '@sentry/angular';
import { SENTRY_CONFIG } from './sentry.config';
import { NSentry } from './sentry.interfaces';
import { SentryService } from './sentry.service';

export function provideDfModuleSentry(
    // You need to provide at least the DSN (from Sentry Panel>project>settings>Client Keys(DSN)) to make it work, the rest is optional.
    config: DeepPartial<Sentry.BrowserOptions>,
    envs?: ('production' | 'uat' | 'staging' | 'development')[] | undefined
): EnvironmentProviders {
    let CONFIG;
    // Check if window is defined to avoid SSR/universal errors, also this functionalty is for browsers only according to Sentry documentation.
    if (typeof window !== 'undefined') {
        const environment = window.dfConfig?.environment;
        const defaultConfig: DeepPartial<Sentry.BrowserOptions> = {
            environment,
            ...NSentry.DEFAULT_CONFIG
        };
        CONFIG = <Sentry.BrowserOptions>defaultsDeep(config || {}, defaultConfig);

        // Sentry initiated here because according to documentation it needs to be initiated before
        // "platformBrowserDynamic().bootstrapModule(AppModule..." from main.ts is executed
        if (CONFIG.dsn && (!envs || envs.includes(environment))) {
            Sentry.init(CONFIG);
        }
    }

    return makeEnvironmentProviders([
        {
            provide: SENTRY_CONFIG,
            useFactory: () => {
                return CONFIG;
            }
        },
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler()
        },
        {
            provide: Sentry.TraceService,
            deps: [Router]
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => {
                const traceService = inject(Sentry.TraceService);
                const sentryService = inject(SentryService);

                void traceService;

                return () => {
                    sentryService.appInitialize();
                };
            },
            multi: true
        }
    ]);
}